import React, { useState, useEffect, useRef } from 'react';
import { LocationData } from '../types';
import L from 'leaflet';

interface MapSearchProps {
  locations: LocationData[];
  onSelect: (loc: LocationData) => void;
  containerClassName?: string;
}

// Interface for geocoding result
interface GeocodingResult {
  id: string;
  name: string;
  displayName: string;
  lat: number;
  lon: number;
  type: 'station' | 'location';
  locObj?: LocationData;
}

// Interface for external result from Nominatim API
interface ExternalResult {
  place_id: number;
  name?: string;
  display_name: string;
  lat: string;
  lon: string;
}

const MapSearch: React.FC<MapSearchProps> = ({ locations, onSelect, containerClassName = '' }) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showResults, setShowResults] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Handle clicks outside search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Search for locations
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    // Search in local data first
    const term = searchTerm.toLowerCase().trim();
    const localResults = locations.filter(loc => 
      loc.name.toLowerCase().includes(term)
    ).map(loc => ({
      id: loc.name,
      name: loc.name,
      displayName: loc.name,
      lat: loc.lat,
      lon: loc.lon,
      type: 'station' as const,
      locObj: loc
    }));
    
    setSearchResults(localResults);
    setShowResults(true);
    
    // Then search external API
    searchTimeout.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(term)}&limit=5`,
          {
            headers: {
              'Accept-Language': 'en-US,en',
              'User-Agent': 'MeteorologicalVisualization'
            }
          }
        );
        
        if (response.ok) {
          const externalResults = await response.json();
          const mappedResults = externalResults.map((result: ExternalResult) => ({
            id: `ext-${result.place_id}`,
            name: result.name || result.display_name.split(',')[0],
            displayName: result.display_name,
            lat: parseFloat(result.lat),
            lon: parseFloat(result.lon),
            type: 'location' as const
          }));
          
          // Combine results, avoiding duplicates
          const combinedResults: GeocodingResult[] = [...localResults];
          
          mappedResults.forEach((extResult: { name: string }) => {
            if (!localResults.some(localResult => 
              localResult.name.toLowerCase() === extResult.name.toLowerCase()
            )) {
              combinedResults.push(extResult as GeocodingResult);
            }
          });
          
          setSearchResults(combinedResults.slice(0, 10));
          setShowResults(true);
        }
      } catch (error) {
        console.error('Error searching locations:', error);
      } finally {
        setIsSearching(false);
      }
    }, 500);
    
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchTerm, locations]);
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };
  
  const handleResultClick = (result: GeocodingResult) => {
    if (result.type === 'station' && result.locObj) {
      onSelect(result.locObj);
    } else {
      // For external results, just zoom the map
      if (typeof (window as any).zoomToMarker === 'function') {
        (window as any).zoomToMarker([result.lat, result.lon]);
      }
    }
    setSearchTerm(result.name);
    setShowResults(false);
  };
  
  return (
    <div ref={searchRef} className={`map-search-container ${containerClassName}`}>
      <div className="map-search-input-wrapper">
        <span className={`search-icon ${isSearching ? 'searching' : ''}`}></span>
        <input
          type="text"
          className="map-search-input"
          placeholder="Search city or location..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>
      {searchResults.length > 0 && (
        <div className={`search-results ${showResults ? 'show' : ''}`}>
          {searchResults.map(result => (
            <div
              key={result.id}
              className={`search-result-item ${result.type}`}
              onClick={() => handleResultClick(result)}
            >
              <div className="search-result-name">{result.name}</div>
              <div className="search-result-info">
                {result.type === 'station' ? (
                  <>{result.displayName}</>
                ) : (
                  <>{result.displayName}</>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {showResults && searchTerm.trim() !== '' && searchResults.length === 0 && (
        <div className={`search-results ${showResults ? 'show' : ''}`}>
          <div className="search-result-item">
            <div className="search-result-name">
              {isSearching ? 'Searching...' : 'No results found'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapSearch; 